'use client';

import { c as _c } from "react/compiler-runtime";
import './video.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoObject } from 'schema-dts';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { VideoProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.ts';
import { getVideoType } from '@sola/utils/video.ts';
import { buildVideoJsonLd } from '../../utils/schema-utils.ts';
import { Image } from '../image/image.tsx';
import { JsonLd } from '../json-ld/json-ld.tsx';
import { VideoFacade } from './video-facade.tsx';
import { getPlayerWidth, postIframeCommand, useVideoIframe } from './video.logic.ts';
type VideoComponentProps = VideoProps & {
  /** Controls whether a VideoObject JSON-LD script is rendered. Defaults to true. */
  renderJsonLd?: boolean;
};
export const Video = t0 => {
  const $ = _c(77);
  const {
    url,
    poster,
    isBackground,
    autoplay: t1,
    allowPlaying: t2,
    width,
    height,
    thumbnailUrl,
    title,
    description,
    uploadDate,
    renderJsonLd: t3
  } = t0;
  const autoplay = t1 === undefined ? false : t1;
  const allowPlaying = t2 === undefined ? true : t2;
  const renderJsonLd = t3 === undefined ? true : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["skippy"];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    t
  } = useTranslation(t4);
  const [hasStarted, setHasStarted] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const [playing, setPlaying] = useState(!!isBackground || allowPlaying && autoplay);
  const [isActivated, setIsActivated] = useState(false);
  const nativeVideoRef = useRef(null);
  const focusIframeOnReadyRef = useRef(false);
  const videoType = getVideoType(url);
  const variableVideoUrl = cookieConsentVideoURL(url);
  const isPortrait = width && height && height > width;
  const aspectRatioStr = width && height ? `${width} / ${height}` : "16/9";
  const t5 = !!isBackground;
  const t6 = !!isPortrait;
  let t7;
  if ($[1] !== t5 || $[2] !== t6) {
    t7 = getPlayerWidth(t5, t6);
    $[1] = t5;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const playerWidth = t7;
  const isProviderVideo = videoType === "vimeo" || videoType === "youtube";
  let t8;
  if ($[4] !== hasStarted) {
    t8 = () => {
      if (!hasStarted) {
        const timeout = setTimeout(() => setHasStarted(true), 100);
        return () => clearTimeout(timeout);
      }
    };
    $[4] = hasStarted;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleVideoStart = t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIframeReady(false);
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const handleIframeLoadStart = t9;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIframeReady(true);
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const handleIframeReady = t10;
  const shouldLoadPlayer = !!isBackground || allowPlaying && autoplay || isActivated;
  let t11;
  if ($[8] !== t) {
    t11 = () => {
      trackClick(t("skippy:video.play"));
      focusIframeOnReadyRef.current = true;
      setIsActivated(true);
    };
    $[8] = t;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const handleActivate = t11;
  const {
    containerRef,
    playerRef: iframeRef
  } = useVideoIframe({
    src: variableVideoUrl,
    videoType,
    isBackground: !!isBackground,
    title: title || t("skippy:video.play"),
    autoplay: autoplay && allowPlaying || isActivated,
    enabled: shouldLoadPlayer,
    onPlay: handleVideoStart,
    onLoadStart: handleIframeLoadStart,
    onReady: handleIframeReady
  });
  let t12;
  let t13;
  if ($[10] !== iframeReady || $[11] !== iframeRef) {
    t12 = () => {
      if (!iframeReady || !focusIframeOnReadyRef.current) {
        return;
      }
      const iframe = iframeRef.current?.iframe;
      if (!iframe) {
        return;
      }
      iframe.focus();
      focusIframeOnReadyRef.current = false;
    };
    t13 = [iframeReady, iframeRef];
    $[10] = iframeReady;
    $[11] = iframeRef;
    $[12] = t12;
    $[13] = t13;
  } else {
    t12 = $[12];
    t13 = $[13];
  }
  useEffect(t12, t13);
  let t14;
  if ($[14] !== allowPlaying || $[15] !== autoplay || $[16] !== iframeReady || $[17] !== iframeRef || $[18] !== isProviderVideo) {
    t14 = () => {
      if (!iframeReady && !nativeVideoRef.current) {
        return;
      }
      if (!allowPlaying) {
        if (isProviderVideo) {
          postIframeCommand(iframeRef.current, "pause");
        } else {
          nativeVideoRef.current?.pause();
        }
        return;
      }
      if (!autoplay) {
        return;
      }
      let timeout_0 = null;
      if (isProviderVideo) {
        timeout_0 = setTimeout(() => {
          postIframeCommand(iframeRef.current, "play");
        }, 100);
      } else {
        nativeVideoRef.current?.play();
      }
      return () => {
        if (timeout_0) {
          clearTimeout(timeout_0);
        }
      };
    };
    $[14] = allowPlaying;
    $[15] = autoplay;
    $[16] = iframeReady;
    $[17] = iframeRef;
    $[18] = isProviderVideo;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== allowPlaying || $[21] !== autoplay || $[22] !== iframeReady || $[23] !== isProviderVideo) {
    t15 = [allowPlaying, iframeReady, autoplay, isProviderVideo];
    $[20] = allowPlaying;
    $[21] = autoplay;
    $[22] = iframeReady;
    $[23] = isProviderVideo;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  useEffect(t14, t15);
  let t16;
  if ($[25] !== iframeRef || $[26] !== isProviderVideo || $[27] !== playing || $[28] !== t) {
    t16 = () => {
      const next = !playing;
      const label = next ? t("skippy:video.play") : t("skippy:video.pause");
      trackClick(label);
      setPlaying(next);
      if (isProviderVideo) {
        postIframeCommand(iframeRef.current, next ? "play" : "pause");
        return;
      }
      if (next) {
        nativeVideoRef.current?.play();
      } else {
        nativeVideoRef.current?.pause();
      }
    };
    $[25] = iframeRef;
    $[26] = isProviderVideo;
    $[27] = playing;
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const handlePlayPauseToggle = t16;
  const t17 = `Video Video--${videoType}`;
  const t18 = isBackground ? "Video--background" : "Video--inline";
  const t19 = isPortrait ? "h-full" : "w-full";
  let t20;
  if ($[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = cn(t17, t18, t19);
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const t21 = !isBackground && renderJsonLd && (poster?.url || thumbnailUrl) && <JsonLd item={{
    "@context": "https://schema.org",
    "@type": "VideoObject",
    ...buildVideoJsonLd({
      name: title || "Video",
      thumbnailUrl: poster?.url || thumbnailUrl,
      contentUrl: variableVideoUrl,
      embedUrl: url,
      description,
      uploadDate
    })
  }} />;
  let t22;
  if ($[34] !== aspectRatioStr || $[35] !== containerRef || $[36] !== handleActivate || $[37] !== height || $[38] !== iframeReady || $[39] !== isBackground || $[40] !== isPortrait || $[41] !== isProviderVideo || $[42] !== playerWidth || $[43] !== poster || $[44] !== shouldLoadPlayer || $[45] !== thumbnailUrl || $[46] !== width) {
    t22 = isProviderVideo && <div ref={containerRef} className={cn("Video__player", {
      "Video__player--portrait": isPortrait,
      "Video__player--landscape": !isPortrait && width,
      "Video__player--default": !width && !height
    })} style={{
      width: playerWidth,
      maxHeight: "100%",
      height: isBackground || isPortrait ? "inherit" : "auto",
      aspectRatio: !isBackground && width && height ? aspectRatioStr : undefined
    }}>{!isBackground && !shouldLoadPlayer && <VideoFacade poster={poster} thumbnailUrl={thumbnailUrl} onActivate={handleActivate} />}{!isBackground && shouldLoadPlayer && !iframeReady && <div style={{
        position: "absolute",
        inset: 0,
        zIndex: 1
      }}><Skeleton width="100%" height="100%" /></div>}</div>;
    $[34] = aspectRatioStr;
    $[35] = containerRef;
    $[36] = handleActivate;
    $[37] = height;
    $[38] = iframeReady;
    $[39] = isBackground;
    $[40] = isPortrait;
    $[41] = isProviderVideo;
    $[42] = playerWidth;
    $[43] = poster;
    $[44] = shouldLoadPlayer;
    $[45] = thumbnailUrl;
    $[46] = width;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== allowPlaying || $[49] !== aspectRatioStr || $[50] !== autoplay || $[51] !== handleVideoStart || $[52] !== height || $[53] !== isBackground || $[54] !== isPortrait || $[55] !== isProviderVideo || $[56] !== playerWidth || $[57] !== variableVideoUrl || $[58] !== width) {
    t23 = !isProviderVideo && <video ref={nativeVideoRef} src={variableVideoUrl} className={cn("Video__player", {
      "Video__player--portrait": isPortrait,
      "Video__player--landscape": !isPortrait && width,
      "Video__player--default": !width && !height
    })} controls={!isBackground} autoPlay={(!!isBackground || autoplay) && allowPlaying} loop={!!isBackground} muted={!!isBackground} playsInline={true} style={{
      width: playerWidth,
      maxHeight: "100%",
      height: isBackground || isPortrait ? "inherit" : "auto",
      aspectRatio: !isBackground && width && height ? aspectRatioStr : undefined
    }} onPlay={() => {
      setPlaying(true);
      handleVideoStart();
    }} onPause={() => setPlaying(false)} />;
    $[48] = allowPlaying;
    $[49] = aspectRatioStr;
    $[50] = autoplay;
    $[51] = handleVideoStart;
    $[52] = height;
    $[53] = isBackground;
    $[54] = isPortrait;
    $[55] = isProviderVideo;
    $[56] = playerWidth;
    $[57] = variableVideoUrl;
    $[58] = width;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== hasStarted || $[61] !== isBackground || $[62] !== poster) {
    t24 = poster && isBackground && <Image className={cn("Video__poster", {
      "Video__poster--hidden": hasStarted
    })} image={poster} priority={true} />;
    $[60] = hasStarted;
    $[61] = isBackground;
    $[62] = poster;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== handlePlayPauseToggle || $[65] !== hasStarted || $[66] !== isBackground || $[67] !== playing || $[68] !== t) {
    t25 = isBackground && hasStarted && <div className="Video__controls absolute bottom-0 right-0 p-comfy-sm"><ButtonStyle variant="tertiary" size="md"><button type="button" onClick={handlePlayPauseToggle}><ArkIcon iconName={playing ? "pause-circle" : "play-circle"} size="20" />{playing ? t("skippy:video.pause") : t("skippy:video.play")}</button></ButtonStyle></div>;
    $[64] = handlePlayPauseToggle;
    $[65] = hasStarted;
    $[66] = isBackground;
    $[67] = playing;
    $[68] = t;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== t20 || $[71] !== t21 || $[72] !== t22 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25) {
    t26 = <div className={t20}>{t21}{t22}{t23}{t24}{t25}</div>;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  return t26;
};